import type {
  AutomationCauseGroupDto,
  AutomationCauseId,
  AutomationRunDto,
  AutomationRunStatus,
} from '@tms/shared';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDot,
  ExternalLink,
  Loader,
  Stethoscope,
  XCircle,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Badge, Card, Table, TableWrap, Td, Th, Tr } from '../../components/ui';
import { useFormat, useT } from '../../i18n';
import type { MessageKey } from '../../i18n';
import { cn } from '../../lib/cn';

/**
 * Pieces every automation page shares: run status, who started a pipeline,
 * the project notice, «why it failed» and the run history table (TestRun
 * src/components/ui.tsx, FailureCauses.tsx, tabs/DashboardTab.tsx HistoryTable).
 */

/* ─── run status ─── */

const STATUS_STYLES: Record<AutomationRunStatus, string> = {
  pending: 'bg-sunken text-ink-muted',
  running: 'bg-accent-soft text-accent-fg',
  success: 'bg-pass-soft text-pass',
  failed: 'bg-fail-soft text-fail',
  cancelled: 'bg-skip-soft text-skip',
};

const STATUS_ICONS: Record<AutomationRunStatus, typeof CircleDot> = {
  pending: CircleDot,
  running: Loader,
  success: CheckCircle2,
  failed: XCircle,
  cancelled: Ban,
};

/** A run's status: never colour alone — an icon and a word too. */
export const AutomationStatusBadge = ({ status }: { status: AutomationRunStatus }) => {
  const { t } = useT();
  const Icon = STATUS_ICONS[status];
  return (
    <Badge className={STATUS_STYLES[status]}>
      <Icon
        className={cn('size-3 shrink-0', status === 'running' && 'animate-spin')}
        aria-hidden="true"
      />
      {t(`automation.status.${status}`)}
    </Badge>
  );
};

/** A stage or run status as an icon, for stage lists. */
export const StageIcon = ({
  status,
  className,
}: {
  status: 'done' | 'active' | 'pending' | AutomationRunStatus;
  className?: string;
}) => {
  const cls = cn('size-4 shrink-0', className);
  if (status === 'done' || status === 'success')
    return <CheckCircle2 className={cn(cls, 'text-pass')} aria-hidden="true" />;
  if (status === 'failed') return <XCircle className={cn(cls, 'text-fail')} aria-hidden="true" />;
  if (status === 'active' || status === 'running')
    return <Loader className={cn(cls, 'animate-spin text-accent')} aria-hidden="true" />;
  if (status === 'cancelled') return <Ban className={cn(cls, 'text-ink-subtle')} aria-hidden="true" />;
  return <CircleDot className={cn(cls, 'text-ink-subtle')} aria-hidden="true" />;
};

const TRIGGERS = ['push', 'schedule', 'web', 'merge_request_event', 'trigger'] as const;

/**
 * Who started a pipeline when it was not started from here: GitLab itself — on
 * a push, a schedule, a button. null for runs started here.
 */
export const useTriggerLabel = () => {
  const { t } = useT();
  return (trigger?: string): string | null => {
    if (!trigger || trigger === 'admin') return null;
    return (TRIGGERS as readonly string[]).includes(trigger)
      ? t(`automation.trigger.${trigger as (typeof TRIGGERS)[number]}`)
      : trigger;
  };
};

/** A team or project mark: its short key on its colour. */
export const Mark = ({
  label,
  color,
  size = 'md',
}: {
  label: string;
  color: string;
  size?: 'sm' | 'md' | 'lg';
}) => (
  <span
    aria-hidden="true"
    className={cn(
      'inline-flex shrink-0 items-center justify-center rounded-md font-semibold text-white',
      size === 'lg' && 'size-11 text-[18px]',
      size === 'md' && 'size-9 text-[14px]',
      size === 'sm' && 'size-5 text-[11px]',
    )}
    style={{ backgroundColor: color }}
  >
    {label}
  </span>
);

/**
 * A project's warning — «this repository holds several teams' tests, don't run
 * it all». Shown wherever a run can be started; an empty note draws nothing.
 */
export const ProjectNotice = ({ note, title }: { note?: string | null; title?: string }) => {
  const { t } = useT();
  if (!note?.trim()) return null;
  return (
    <div
      role="note"
      className="flex items-start gap-2 rounded-lg border border-block/40 bg-block-soft px-3 py-2 text-[13px] text-ink"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-block" aria-hidden="true" />
      <p>
        <span className="font-semibold">{title ?? t('automation.notice')}</span>{' '}
        <span className="whitespace-pre-wrap">{note}</span>
      </p>
    </div>
  );
};

/* ─── why it failed ─── */

export const CAUSE_COLORS: Record<AutomationCauseId, { bar: string; text: string; border: string }> = {
  product: { bar: 'bg-fail', text: 'text-fail', border: 'border-fail' },
  stand: { bar: 'bg-block', text: 'text-block', border: 'border-block' },
  login: { bar: 'bg-accent', text: 'text-accent', border: 'border-accent' },
  access: { bar: 'bg-skip', text: 'text-skip', border: 'border-skip' },
  autotest: { bar: 'bg-ink-subtle', text: 'text-ink-muted', border: 'border-ink-subtle' },
};

/** A test's cause on the run page. */
export const CauseBadge = ({ cause }: { cause?: AutomationCauseId | undefined }) => {
  const { t } = useT();
  if (!cause) return null;
  return (
    <Badge
      className={cn('border bg-surface', CAUSE_COLORS[cause].text, CAUSE_COLORS[cause].border)}
      title={t('automation.cause.byText')}
    >
      {t(`automation.cause.${cause}`)}
    </Badge>
  );
};

/**
 * Red tests of a run by cause — the product, the stand, login and OTP, access,
 * the autotest itself. The server reads the cause from the error text: a hint
 * of who should look, not a verdict — every test shows its error.
 */
export const FailureCauses = ({
  causes,
  red,
  subtitle,
  action,
}: {
  causes: AutomationCauseGroupDto[];
  /** red tests in the run */
  red: number;
  subtitle?: string;
  action?: ReactNode;
}) => {
  const { t } = useT();
  const [open, setOpen] = useState<AutomationCauseId | null>(null);
  if (!causes.length) return null;

  const byOwner = new Map<string, number>();
  for (const c of causes) byOwner.set(c.owner, (byOwner.get(c.owner) ?? 0) + c.count);
  const owners = [...byOwner].sort((a, b) => b[1] - a[1]);
  const verdict =
    owners.length === 1 && owners[0]
      ? t('automation.cause.allOwner', { count: red, owner: owners[0][0] })
      : t('automation.cause.owners', {
          list: owners.map(([owner, n]) => `${owner} — ${n}`).join(', '),
        });

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h3 className="flex items-center gap-2 text-[14px] font-semibold text-ink">
          <Stethoscope className="size-4" aria-hidden="true" />
          {t('automation.cause.title')}
          {subtitle ? <span className="font-normal text-ink-muted">· {subtitle}</span> : null}
        </h3>
        {action}
      </div>
      <div className="space-y-3 px-4 py-3">
        <div className="flex h-2.5 overflow-hidden rounded-md bg-sunken" aria-hidden="true">
          {causes.map((c) => (
            <div
              key={c.id}
              title={`${c.label}: ${c.count}`}
              className={cn('min-w-1', CAUSE_COLORS[c.id].bar)}
              style={{ flex: `${c.count} 0 0` }}
            />
          ))}
        </div>
        <p className="text-[13px] text-ink-muted">{verdict}</p>
        <ul className="space-y-2">
          {causes.map((c) => {
            const isOpen = open === c.id;
            const color = CAUSE_COLORS[c.id];
            return (
              <li
                key={c.id}
                className={cn('rounded-lg border', isOpen ? color.border : 'border-border')}
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : c.id)}
                  className="flex w-full flex-wrap items-center justify-between gap-3 px-3 py-2 text-left"
                >
                  <span className="flex min-w-0 flex-[1_1_240px] items-center gap-2">
                    {isOpen ? (
                      <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
                    ) : (
                      <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
                    )}
                    <span className={cn('size-2.5 shrink-0 rounded-sm', color.bar)} aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold text-ink">
                        {t(`automation.cause.${c.id}`)}
                      </span>
                      <span className="block text-[12px] text-ink-muted">
                        {t(`automation.cause.${c.id}.hint`)}
                      </span>
                    </span>
                  </span>
                  <span className="ml-auto flex shrink-0 items-center gap-3">
                    <Badge>{c.owner}</Badge>
                    <span className={cn('min-w-7 text-right text-[18px] font-bold', color.text)}>
                      {c.count}
                    </span>
                    <span className="min-w-9 text-right text-[12px] text-ink-subtle">
                      {Math.round((c.count / Math.max(red, 1)) * 100)}%
                    </span>
                  </span>
                </button>
                {isOpen ? (
                  <ul className="space-y-2 px-3 pb-3 pl-10">
                    {c.tests.map((test) => (
                      <li key={test.key} className="border-t border-border pt-2">
                        <p className="text-[13px] font-semibold text-ink">{test.name}</p>
                        <p className="text-[12px] text-ink-muted">{test.displayName || test.className}</p>
                        {test.message ? (
                          <p className={cn('whitespace-pre-wrap break-words text-[12px]', color.text)}>
                            {test.message}
                          </p>
                        ) : null}
                      </li>
                    ))}
                    {c.count > c.tests.length ? (
                      <li className="text-[12px] text-ink-subtle">
                        {t('automation.cause.more', { count: c.count - c.tests.length })}
                      </li>
                    ) : null}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
};

/* ─── run history ─── */

/** What a run ran, in a few words: all tests, a suite, or how many classes. */
export const useRunScope = () => {
  const { t } = useT();
  return (run: AutomationRunDto): string => {
    const config = run.config;
    if (config.type === 'suite' && config.suite) return t('automation.scope.suite', { suite: config.suite });
    if (config.type === 'classes') return t('automation.scope.classes', { count: config.classes?.length ?? 0 });
    return t('automation.scope.all');
  };
};

/** Runs of a project or a team; a row opens the run page. */
export const RunHistoryTable = ({
  runs,
  showProject = false,
  emptyKey = 'automation.history.empty',
}: {
  runs: AutomationRunDto[];
  showProject?: boolean;
  emptyKey?: MessageKey;
}) => {
  const { t } = useT();
  const { relative } = useFormat();
  const navigate = useNavigate();
  const triggerLabel = useTriggerLabel();
  const scope = useRunScope();

  if (!runs.length) {
    return <p className="px-4 py-6 text-center text-[13px] text-ink-muted">{t(emptyKey)}</p>;
  }
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th>{t('automation.history.id')}</Th>
            {showProject ? <Th>{t('automation.columnProject')}</Th> : null}
            <Th>{t('automation.columnStartedBy')}</Th>
            <Th>{t('automation.history.scope')}</Th>
            <Th>{t('automation.history.env')}</Th>
            <Th>{t('automation.columnStarted')}</Th>
            <Th>{t('automation.history.status')}</Th>
            <Th>{t('automation.history.report')}</Th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            const byGitlab = triggerLabel(run.trigger);
            const report = run.allureUrl ?? run.reportUrl;
            return (
              <Tr
                key={run.id}
                onClick={() =>
                  void navigate({
                    to: '/automation/runs/$runId',
                    params: { runId: run.pipelineId },
                    search: { project: run.projectId },
                  })
                }
              >
                <Td className="font-mono font-semibold">{run.id}</Td>
                {showProject ? <Td>{run.projectName}</Td> : null}
                <Td>
                  {byGitlab ? (
                    <Badge title={t('automation.trigger.hint')}>{byGitlab}</Badge>
                  ) : (
                    run.user
                  )}
                </Td>
                <Td className="text-ink-muted">{byGitlab ? '—' : scope(run)}</Td>
                <Td className="font-mono text-[12px]">{run.config.environment ?? '—'}</Td>
                <Td className="text-ink-muted" >{relative(run.createdAt)}</Td>
                <Td>
                  <AutomationStatusBadge status={run.status} />
                </Td>
                <Td>
                  {report ? (
                    <a
                      href={report}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      className="inline-flex items-center gap-1 text-accent hover:underline"
                      title={run.allureUrl ? 'Allure' : t('automation.history.gradleReport')}
                    >
                      {run.allureUrl ? 'Allure' : t('automation.history.reportShort')}
                      <ExternalLink className="size-3" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="text-ink-subtle">—</span>
                  )}
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </TableWrap>
  );
};
